import { APP_STYLE_CONFIG, SCORE_LABELS } from '@/constants';
import { cn } from '@/utils';
import { ScoreHistoryCardEmptyState } from './subcomponents/ScoreHistoryCardEmptyState';
import { ScoreHistoryCardItemList } from './subcomponents/ScoreHistoryCardItemList';
import { ScoreHistoryCardLoader } from './subcomponents/ScoreHistoryCardLoader';
import { ScoreHistoryCardShowMoreButton } from './subcomponents/ScoreHistoryCardShowMoreButton';
import { ScoreHistoryCardTitle } from './subcomponents/ScoreHistoryCardTitle';
import { ScoreHistoryCardProps } from './ScoreHistoryCard.types';
import { useScoreHistoryCard } from './features';
import * as styles from './ScoreHistoryCard.styles';
import React, { useId } from 'react';

/**
 * A card component that displays a list of previous scores with dates and times.
 * Features a "Show more" functionality to expand/collapse the list.
 */
export const ScoreHistoryCard: React.FC<ScoreHistoryCardProps> = ({
  allVisible = false,
  className,
  entries,
  initialVisibleCount = 3,
  isLoading = false,
  onShowMore,
  showMoreButton = true,
  simulationType,
  title = SCORE_LABELS.PREVIOUS_SCORES,
  withCardBorder = APP_STYLE_CONFIG.showCardBorder,
}) => {
  // =============================================================
  // Feature Hook
  // =============================================================
  const { ariaExpandedLabel, handleShowMore, isExpanded, shouldShowMoreButton, visibleEntries } =
    useScoreHistoryCard({
      allVisible,
      entries,
      initialVisibleCount,
      onShowMore,
      showMoreButton,
    });

  // Stable unique id linking the list to the show-more button via aria-controls.
  const listId = useId();

  // =============================================================
  // Render the loading state
  // =============================================================
  if (isLoading) {
    return (
      <ScoreHistoryCardLoader
        className={className}
        initialVisibleCount={initialVisibleCount}
        title={title}
        withCardBorder={withCardBorder}
      />
    );
  }

  // =============================================================
  // Render the empty state when there are no entries to display
  // =============================================================
  if (entries.length === 0) {
    return (
      <ScoreHistoryCardEmptyState
        className={className}
        title={title}
        withCardBorder={withCardBorder}
      />
    );
  }

  // =============================================================
  // Render the score history card with the list of entries
  // and the "Show more" button if needed
  // =============================================================
  return (
    <div
      aria-label={title}
      className={cn(styles.scoreHistoryCardStyles({ bordered: withCardBorder }), className)}
      role="region"
    >
      {/* Title */}
      <ScoreHistoryCardTitle isLoading={isLoading} title={title} />

      {/* Score History List */}
      <div className={styles.scoreHistoryListStyles}>
        <ScoreHistoryCardItemList
          id={listId}
          entries={visibleEntries}
          simulationType={simulationType}
        />

        {/* Show More Button */}
        {shouldShowMoreButton && (
          <ScoreHistoryCardShowMoreButton
            ariaLabel={ariaExpandedLabel}
            controlsId={listId}
            isExpanded={isExpanded}
            onClick={handleShowMore}
          />
        )}
      </div>
    </div>
  );
};

ScoreHistoryCard.displayName = 'ScoreHistoryCard';

export default ScoreHistoryCard;
